<template>
  <div class="watermark-manager">
    <h2>Wasserzeichen-Einstellungen</h2>
    <p class="intro">Hier können Sie ein Wasserzeichen für Fan-Fotos konfigurieren.</p>
    
    <div v-if="isLoading" class="loading-state">
      <div class="spinner"></div>
      <p>Lade Einstellungen...</p>
    </div>
    
    <div v-else class="watermark-settings">
      <!-- Aktuelles Wasserzeichen und Upload -->
      <div class="settings-section">
        <h3>Wasserzeichen-Bild</h3>
        
        <div class="current-watermark">
          <div v-if="settings.image_exists" class="watermark-preview">
            <img :src="settings.image_url" alt="Aktuelles Wasserzeichen" />
          </div>
          <div v-else class="no-watermark">
            <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10 2h4l2 4h4a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8c0-1.1.9-2 2-2h4l2-4z"/>
              <circle cx="12" cy="13" r="3" />
            </svg>
            <p>Kein Wasserzeichen vorhanden</p>
          </div>
        </div>
        
        <div class="watermark-upload">
          <p>Laden Sie ein PNG-Bild mit Transparenz als Wasserzeichen hoch:</p>
          <form @submit.prevent="uploadWatermark" class="upload-form">
            <div class="file-input-container">
              <input 
                type="file" 
                id="watermark" 
                ref="fileInput"
                @change="handleFileChange" 
                accept="image/png"
                required
              />
              <div v-if="previewUrl" class="file-preview">
                <img :src="previewUrl" alt="Vorschau" />
                <button type="button" @click="removeImage" class="remove-button">
                  <i class="fas fa-times"></i>
                </button>
              </div>
              <div 
                v-else 
                class="custom-file-button" 
                @click="triggerFileInput"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
                <span>PNG-Datei auswählen</span>
              </div>
            </div>
            
            <button 
              type="submit" 
              class="upload-button" 
              :disabled="isUploading || !selectedFile"
            >
              <span v-if="isUploading">
                <svg class="spinner" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
                </svg>
                Wird hochgeladen...
              </span>
              <span v-else>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
                Hochladen
              </span>
            </button>
          </form>
          
          <div v-if="uploadError" class="error-message">
            {{ uploadError }}
          </div>
          
          <div v-if="uploadSuccess" class="success-message">
            {{ uploadSuccess }}
          </div>
        </div>
      </div>
      
      <!-- Position und Größe des Wasserzeichens -->
      <div class="settings-section">
        <h3>Wasserzeichen-Konfiguration</h3>
        
        <div class="settings-form">
          <!-- Position -->
          <div class="form-group">
            <label for="position">Position:</label>
            <select v-model="settings.position" id="position" :disabled="!settings.image_exists">
              <option value="top-left">Oben links</option>
              <option value="top-right">Oben rechts</option>
              <option value="bottom-left">Unten links</option>
              <option value="bottom-right">Unten rechts</option>
              <option value="center">Mitte</option>
            </select>
          </div>
          
          <!-- Deckkraft -->
          <div class="form-group">
            <label for="opacity">Deckkraft: {{ settings.opacity }}%</label>
            <input 
              type="range" 
              id="opacity" 
              v-model.number="settings.opacity" 
              min="10" 
              max="100" 
              step="1"
              :disabled="!settings.image_exists"
            />
          </div>
          
          <!-- Abstand vom Rand -->
          <div class="form-group">
            <label for="padding">Abstand vom Rand: {{ settings.padding }}px</label>
            <input 
              type="range" 
              id="padding" 
              v-model.number="settings.padding" 
              min="0" 
              max="100" 
              step="1"
              :disabled="!settings.image_exists"
            />
          </div>
          
          <!-- Größe des Wasserzeichens -->
          <div class="form-group">
            <label for="size">Größe des Wasserzeichens: {{ settings.size }}%</label>
            <input 
              type="range" 
              id="size" 
              v-model.number="settings.size" 
              min="1" 
              max="100" 
              step="1"
              :disabled="!settings.image_exists"
            />
          </div>
          
          <!-- Aktivieren/Deaktivieren -->
          <div class="form-group toggle-group">
            <label for="enabled">Wasserzeichen aktivieren:</label>
            <div class="toggle-switch" :class="{ disabled: !settings.image_exists }">
              <input 
                type="checkbox" 
                id="enabled" 
                v-model="settings.enabled"
                :disabled="!settings.image_exists"
              />
              <label for="enabled" class="toggle"></label>
            </div>
          </div>
          
          <button 
            @click="saveSettings" 
            class="save-button" 
            :disabled="isSaving || !settings.image_exists"
          >
            <span v-if="isSaving">
              <svg class="spinner" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
              </svg>
              Speichern...
            </span>
            <span v-else>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                <polyline points="17 21 17 13 7 13 7 21"></polyline>
                <polyline points="7 3 7 8 15 8"></polyline>
              </svg>
              Einstellungen speichern
            </span>
          </button>
          
          <div v-if="saveError" class="error-message">
            {{ saveError }}
          </div>
          
          <div v-if="saveSuccess" class="success-message">
            {{ saveSuccess }}
          </div>
        </div>
      </div>
      
      <!-- Vorschau -->
      <div class="settings-section" v-if="settings.image_exists">
        <h3>Live-Vorschau</h3>
        <p>So wird das Wasserzeichen auf Fan-Fotos angewendet:</p>
        
        <div class="watermark-demo">
          <div class="demo-image">
            <div v-if="isPreviewLoading" class="loading-preview">
              <div class="spinner"></div>
              <p>Vorschau wird geladen...</p>
            </div>
            <img 
              v-else
              :src="previewImageUrl || demoImageUrl" 
              alt="Vorschau mit Wasserzeichen" 
              class="preview-image"
            />
          </div>
          
          <button @click="generatePreview" class="refresh-preview-button" :disabled="isPreviewLoading">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38"/>
            </svg>
            {{ randomPhotoUrl ? 'Neues Beispielbild laden' : 'Beispiel generieren' }}
          </button>
        </div>
        
        <p class="demo-note">Die Vorschau zeigt ein zufälliges Fan-Foto mit Ihren aktuellen Wasserzeichen-Einstellungen.</p>
      </div>
    </div>
  </div>
</template>

<script>
// Demo-Bild importieren
import demoImage from '../../assets/demo-photo.js';

export default {
  name: 'WatermarkManager',
  data() {
    return {
      demoImageUrl: demoImage, // Verwendung des importierten Bildes
      isLoading: true,
      isPreviewLoading: false,
      settings: {
        id: null,
        watermark_image: '',
        position: 'bottom-right',
        opacity: 70,
        padding: 10,
        size: 40,
        enabled: false,
        image_exists: false,
        image_url: ''
      },
      selectedFile: null,
      previewUrl: null,
      previewImageUrl: null,
      randomPhotoUrl: null,
      isUploading: false,
      isSaving: false,
      uploadError: null,
      uploadSuccess: null,
      saveError: null,
      saveSuccess: null
    }
  },
  mounted() {
    this.loadSettings();
  },
  watch: {
    // Wenn sich Einstellungen ändern, Vorschau aktualisieren
    'settings.position': function() { this.updatePreview(); },
    'settings.opacity': function() { this.updatePreview(); },
    'settings.padding': function() { this.updatePreview(); },
    'settings.size': function() { this.updatePreview(); }
  },
  methods: {
    async loadSettings() {
      this.isLoading = true;
      try {
        const response = await fetch('/api/watermark_settings.php');
        if (!response.ok) {
          throw new Error('Fehler beim Laden der Einstellungen');
        }
        
        const data = await response.json();
        if (data.success) {
          this.settings = {
            id: data.data.id,
            watermark_image: data.data.watermark_image,
            position: data.data.position,
            opacity: parseInt(data.data.opacity),
            padding: parseInt(data.data.padding),
            size: data.data.size ? parseInt(data.data.size) : 40,
            enabled: data.data.enabled === '1' || data.data.enabled === 1,
            image_exists: data.data.watermark_image ? true : false,
            image_url: data.data.watermark_image
          };
          
          // Zufälliges Bild für die Vorschau laden
          this.loadRandomPhoto();
        }
      } catch (error) {
        console.error('Fehler beim Laden der Einstellungen:', error);
      } finally {
        this.isLoading = false;
      }
    },

    async loadRandomPhoto() {
      try {
        const response = await fetch('/api/get_random_photo.php');
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.imageUrl) {
            this.randomPhotoUrl = data.imageUrl;
            // Initiale Vorschau generieren
            this.generatePreview();
          }
        }
      } catch (error) {
        console.error('Fehler beim Laden eines zufälligen Fotos:', error);
      }
    },

    async generatePreview() {
      if (!this.settings.image_exists || !this.randomPhotoUrl) {
        return;
      }
      
      this.isPreviewLoading = true;
      this.previewImageUrl = null;
      
      try {
        const response = await fetch('/api/preview_watermark.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            imageUrl: this.randomPhotoUrl,
            position: this.settings.position,
            opacity: this.settings.opacity,
            padding: this.settings.padding,
            size: this.settings.size
          }),
        });
        
        if (!response.ok) {
          throw new Error('Fehler bei der Vorschau-Generierung');
        }
        
        const data = await response.json();
        if (data.success && data.previewUrl) {
          this.previewImageUrl = data.previewUrl;
        }
      } catch (error) {
        console.error('Fehler bei der Vorschau-Generierung:', error);
      } finally {
        this.isPreviewLoading = false;
      }
    },

    updatePreview() {
      // Wir prüfen, ob die Vorschau schon einmal generiert wurde
      if (this.previewImageUrl) {
        // Debounce-Implementation mit setTimeout
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
        }
        
        this.debounceTimer = setTimeout(() => {
          this.generatePreview();
        }, 500); // 500ms Verzögerung
      }
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    
    handleFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      this.selectedFile = file;
      
      // Vorschaubild erstellen
      const reader = new FileReader();
      reader.onload = e => {
        this.previewUrl = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    
    removeImage() {
      this.selectedFile = null;
      this.previewUrl = null;
      this.$refs.fileInput.value = '';
    },
    
    async uploadWatermark() {
      if (!this.selectedFile) return;
      
      this.isUploading = true;
      this.uploadError = null;
      this.uploadSuccess = null;
      
      try {
        // Wasserzeichen als Base64 senden
        const reader = new FileReader();
        reader.readAsDataURL(this.selectedFile);
        
        reader.onload = async () => {
          const base64data = reader.result;
          
          const response = await fetch('/api/watermark_settings.php', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              watermarkImageData: base64data
            }),
          });
          
          if (!response.ok) {
            throw new Error('Fehler beim Hochladen');
          }
          
          const data = await response.json();
          if (data.success) {
            this.uploadSuccess = 'Wasserzeichen erfolgreich hochgeladen';
            this.selectedFile = null;
            this.previewUrl = null;
            this.$refs.fileInput.value = '';
            
            // Einstellungen neu laden
            await this.loadSettings();
            
            // Nach kurzer Zeit Erfolgsmeldung ausblenden
            setTimeout(() => {
              this.uploadSuccess = null;
            }, 3000);
          } else {
            throw new Error(data.error || 'Unbekannter Fehler');
          }
        };
      } catch (error) {
        this.uploadError = `Fehler: ${error.message}`;
        console.error('Upload-Fehler:', error);
        
        // Nach kurzer Zeit Fehlermeldung ausblenden
        setTimeout(() => {
          this.uploadError = null;
        }, 5000);
      } finally {
        this.isUploading = false;
      }
    },
    
    async saveSettings() {
      this.isSaving = true;
      this.saveError = null;
      this.saveSuccess = null;
      
      try {
        const response = await fetch('/api/watermark_settings.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            enabled: this.settings.enabled,
            position: this.settings.position,
            opacity: this.settings.opacity,
            padding: this.settings.padding,
            size: this.settings.size
          }),
        });
        
        if (!response.ok) {
          throw new Error('Fehler beim Speichern');
        }
        
        const data = await response.json();
        if (data.success) {
          this.saveSuccess = 'Einstellungen erfolgreich gespeichert';
          
          // Nach kurzer Zeit Erfolgsmeldung ausblenden
          setTimeout(() => {
            this.saveSuccess = null;
          }, 3000);
        } else {
          throw new Error(data.error || 'Unbekannter Fehler');
        }
      } catch (error) {
        this.saveError = `Fehler: ${error.message}`;
        console.error('Speicher-Fehler:', error);
        
        // Nach kurzer Zeit Fehlermeldung ausblenden
        setTimeout(() => {
          this.saveError = null;
        }, 5000);
      } finally {
        this.isSaving = false;
      }
    },
    
    getPositionStyle(property) {
      switch (this.settings.position) {
        case 'top-left':
          if (property === 'top') return `${this.settings.padding}px`;
          if (property === 'left') return `${this.settings.padding}px`;
          if (property === 'right') return 'auto';
          if (property === 'bottom') return 'auto';
          if (property === 'transform') return 'none';
          break;
        case 'top-right':
          if (property === 'top') return `${this.settings.padding}px`;
          if (property === 'left') return 'auto';
          if (property === 'right') return `${this.settings.padding}px`;
          if (property === 'bottom') return 'auto';
          if (property === 'transform') return 'none';
          break;
        case 'bottom-left':
          if (property === 'top') return 'auto';
          if (property === 'left') return `${this.settings.padding}px`;
          if (property === 'right') return 'auto';
          if (property === 'bottom') return `${this.settings.padding}px`;
          if (property === 'transform') return 'none';
          break;
        case 'center':
          if (property === 'top') return '50%';
          if (property === 'left') return '50%';
          if (property === 'right') return 'auto';
          if (property === 'bottom') return 'auto';
          if (property === 'transform') return 'translate(-50%, -50%)';
          break;
        case 'bottom-right':
        default:
          if (property === 'top') return 'auto';
          if (property === 'left') return 'auto';
          if (property === 'right') return `${this.settings.padding}px`;
          if (property === 'bottom') return `${this.settings.padding}px`;
          if (property === 'transform') return 'none';
          break;
      }
      
      return 'auto';
    }
  }
}
</script>

<style scoped>
.watermark-manager {
  padding: 20px;
}

h2 {
  margin-bottom: 20px;
  color: var(--primary-color);
}

.intro {
  margin-bottom: 30px;
  color: var(--text-muted);
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px 0;
}

.spinner {
  animation: rotate 2s linear infinite;
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

.settings-section {
  background-color: var(--white);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  box-shadow: var(--shadow-sm);
}

h3 {
  margin-bottom: 15px;
  color: var(--text-color);
  font-size: 1.2rem;
}

.current-watermark {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  border: 2px dashed #ddd;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  min-height: 150px;
}

.watermark-preview img {
  max-width: 300px;
  max-height: 150px;
  display: block;
}

.no-watermark {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
}

.no-watermark svg {
  opacity: 0.5;
  margin-bottom: 10px;
}

.watermark-upload {
  margin-top: 15px;
}

.file-input-container {
  margin-bottom: 15px;
}

input[type="file"] {
  display: none;
}

.custom-file-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background-color: #f5f5f5;
  border: 2px dashed #ddd;
  border-radius: 8px;
  padding: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.custom-file-button:hover {
  background-color: #eee;
}

.custom-file-button svg {
  color: var(--primary-color);
}

.file-preview {
  position: relative;
  margin-bottom: 15px;
  border-radius: 8px;
  overflow: hidden;
  max-width: 300px;
  box-shadow: var(--shadow-sm);
}

.file-preview img {
  display: block;
  width: 100%;
  height: auto;
}

.remove-button {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.remove-button:hover {
  background-color: rgba(0, 0, 0, 0.8);
}

.upload-button,
.save-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 15px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.upload-button:hover,
.save-button:hover {
  background-color: var(--primary-dark);
}

.upload-button:disabled,
.save-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.error-message {
  color: #e53935;
  margin-top: 10px;
  padding: 10px;
  background-color: rgba(229, 57, 53, 0.1);
  border-radius: 4px;
}

.success-message {
  color: #43a047;
  margin-top: 10px;
  padding: 10px;
  background-color: rgba(67, 160, 71, 0.1);
  border-radius: 4px;
}

.settings-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

label {
  font-weight: 500;
  color: var(--text-color);
}

select {
  padding: 10px;
  border-radius: 4px;
  border: 1px solid #ddd;
  background-color: white;
}

input[type="range"] {
  width: 100%;
  height: 6px;
  appearance: none;
  background-color: #ddd;
  border-radius: 3px;
  outline: none;
}

input[type="range"]::-webkit-slider-thumb {
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: var(--primary-color);
  cursor: pointer;
}

.toggle-group {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.toggle-switch {
  position: relative;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-switch .toggle {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
  background-color: #ccc;
  border-radius: 24px;
  transition: all 0.3s;
  cursor: pointer;
}

.toggle-switch .toggle::after {
  content: '';
  position: absolute;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: white;
  top: 2px;
  left: 2px;
  transition: all 0.3s;
}

.toggle-switch input:checked + .toggle {
  background-color: var(--primary-color);
}

.toggle-switch input:checked + .toggle::after {
  left: calc(100% - 22px);
}

.toggle-switch.disabled .toggle {
  opacity: 0.5;
  cursor: not-allowed;
}

.watermark-demo {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.demo-image {
  position: relative;
  width: 100%;
  max-width: 600px;
  margin: 0 auto 20px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.loading-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background-color: #f5f5f5;
  min-height: 200px;
}

.preview-image {
  width: 100%;
  height: auto;
  display: block;
  max-height: none;
  object-fit: contain;
}

.refresh-preview-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 15px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 10px;
}

.refresh-preview-button:hover {
  background-color: var(--primary-dark);
}

.refresh-preview-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.demo-note {
  margin-top: 15px;
  font-size: 0.9rem;
  color: var(--text-muted);
  text-align: center;
}

@media (max-width: 768px) {
  .settings-section {
    padding: 15px;
  }
  
  .toggle-group {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style> 